import { useEffect, useMemo, useState } from 'react'
import Spinner from '~/components/Spinner'
import { listPackContents, PackWeapon } from '../api/packs'
import { rarityColor, rarityRank } from './PackOpening'

/** How many skins the modal shows. This is a highlight reel, not an inventory:
 *  a grid of everything is a spreadsheet, and the tail of it is the stuff
 *  nobody opens a pack hoping for. Twelve fills three rows at the widest
 *  breakpoint without the dialog needing to scroll. */
const TOP_N = 12

const SkinTile = ({ w }: { w: PackWeapon }) => {
    const color = rarityColor(w.rarity)
    return (
        <div
            className='flex flex-col overflow-hidden border border-white/10 bg-black/50'
            style={{ borderTop: `2px solid ${color}` }}
        >
            <div className='relative flex aspect-square items-center justify-center overflow-hidden bg-[radial-gradient(circle,rgba(255,255,255,0.05),transparent_70%)]'>
                {w.gif_uri ? (
                    <img src={w.gif_uri} alt={w.name ?? ''} className='max-h-full max-w-full object-contain' />
                ) : (
                    <span className='text-2xs uppercase tracking-widest text-white/30'>no preview</span>
                )}
            </div>
            <div className='flex flex-col gap-0.5 border-t border-white/10 px-2.5 py-2'>
                <span className='truncate text-xs font-black uppercase tracking-wide' style={{ color }}>
                    {w.rarity ?? 'Skin'}
                </span>
                <span className='truncate text-2xs font-bold uppercase tracking-[0.1em] text-white/60'>
                    {w.name ?? '—'}
                </span>
                {w.weapon_type && (
                    <span className='truncate text-2xs uppercase tracking-[0.1em] text-white/35'>
                        {w.weapon_type}
                        {w.erb ? ` · +${w.erb}` : ''}
                    </span>
                )}
            </div>
        </div>
    )
}

/**
 * "Top skins": the best of what a pack can yield, as a grid.
 *
 * This replaced a full "what's inside" listing of the whole drawable pool. The
 * pool is the honest answer to "what can I win" and stays the API's job, but as
 * a screen it sold the pack on its worst contents as much as its best — the
 * grid opened on however many commons happened to sort first. A player deciding
 * whether to spend wants to see the ceiling, so this shows the rarest `TOP_N`
 * and says plainly that it is a selection, not the stock list.
 */
export const TopSkinsModal = ({
    packId,
    packName,
    contents,
}: {
    packId: number
    packName: string
    /** Preview/harness path: render these and never hit the API, the same
     *  escape hatch `LiveFeed` takes. Without it the design harness -- whose
     *  whole point is that it runs with no Django behind it -- opened this
     *  modal onto a refused request and an empty grid. */
    contents?: PackWeapon[]
}) => {
    const [items, setItems] = useState<PackWeapon[] | null>(contents ?? null)

    useEffect(() => {
        if (contents) {
            setItems(contents)
            return
        }
        let alive = true
        listPackContents(packId)
            .then((list) => alive && setItems(list))
            .catch(() => alive && setItems([]))
        return () => {
            alive = false
        }
    }, [packId, contents])

    // Rarest first, then the best of that; ties break by name so the order is
    // stable between opens rather than following whatever the API returned.
    const top = useMemo(
        () =>
            items === null
                ? null
                : [...items]
                      .sort(
                          (a, b) =>
                              rarityRank(a.rarity) - rarityRank(b.rarity) ||
                              (a.name ?? '').localeCompare(b.name ?? ''),
                      )
                      .slice(0, TOP_N),
        [items],
    )

    return (
        <div className='flex max-h-[80vh] flex-col gap-4'>
            <div className='flex items-baseline gap-3'>
                <h2 className='text-lg font-black uppercase tracking-[0.14em]'>Top skins</h2>
                <span className='text-xs font-bold uppercase tracking-[0.15em] text-white/40'>
                    {packName}
                    {/* Only worth saying when there is more behind the grid --
                        "the best 12 of 12" would be a boast about nothing. */}
                    {items && items.length > TOP_N ? ` · best ${top?.length} of ${items.length}` : ''}
                </span>
            </div>

            {top === null ? (
                <div className='flex items-center justify-center py-16'>
                    <Spinner className='h-8 w-8' />
                </div>
            ) : top.length === 0 ? (
                <div className='py-16 text-center text-sm font-bold uppercase tracking-[0.15em] text-white/40'>
                    This pack is empty.
                </div>
            ) : (
                <div className='grid grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 md:grid-cols-4'>
                    {top.map((w, i) => (
                        <SkinTile key={w.mint ?? `skin-${w.name}-${i}`} w={w} />
                    ))}
                </div>
            )}
        </div>
    )
}
